import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogTitle,
} from "@/shared/ui/alert-dialog";
import { Button } from "@/shared/ui/button";

const SESSION_EXPIRED_KEY = 'session_expired_reason';

export type SessionExpiredReason = 'idle' | 'absolute' | 'security' | null;

// Helper to set reason (called from SessionTimeoutProvider or api.ts on 401)
export const setSessionExpiredReason = (reason: 'idle' | 'absolute' | 'security') => {
    sessionStorage.setItem(SESSION_EXPIRED_KEY, reason);
};

// Helper to get and clear reason
const getAndClearReason = (): SessionExpiredReason => {
    const reason = sessionStorage.getItem(SESSION_EXPIRED_KEY) as SessionExpiredReason;
    if (reason) {
        sessionStorage.removeItem(SESSION_EXPIRED_KEY);
    }
    return reason;
};

export const SessionExpiredDialog: React.FC = () => {
    const { t } = useTranslation('auth');
    const [reason, setReason] = useState<SessionExpiredReason>(null);

    useEffect(() => {
        const storedReason = getAndClearReason();
        if (storedReason) {
            setReason(storedReason);
        }
    }, []);

    const handleClose = () => {
        setReason(null);
    };

    if (!reason) return null;

    const isIdle = reason === 'idle';
    const isSecurity = reason === 'security';

    const message = isIdle
        ? t('session.sessionEndedIdle')
        : isSecurity
            ? t('session.sessionEndedSecurity')
            : t('session.sessionEndedAbsolute');

    return (
        <AlertDialog open={true}>
            <AlertDialogContent ariaComplete className="w-[calc(100%-1rem)] sm:max-w-sm gap-0 p-4 sm:p-6 shadow-2xl ring-2 ring-gray-200/80 bg-white">
                <div className="text-center mb-4">
                    <AlertDialogTitle className="text-base sm:text-lg font-semibold text-gray-900">
                        {t('session.sessionEnded')}
                    </AlertDialogTitle>
                </div>

                <AlertDialogDescription className="text-sm text-gray-600 text-center mb-6">
                    {message}
                </AlertDialogDescription>

                <AlertDialogFooter>
                    <Button onClick={handleClose}>
                        {t('session.ok')}
                    </Button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};
